import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  accountMappingService,
  type MappingImportData,
} from "~/services/contabilidad/accountMappingService";
import type {
  AccountMappingFilters,
  AccountMappingFormData,
} from "~/types/contabilidad";
import { MAPPING_TEMPLATES_QUERY_KEY } from "./useMappingTemplates";

export const ACCOUNT_MAPPINGS_QUERY_KEY = ["accountMappings"];

export function useAccountMappings(filters?: AccountMappingFilters) {
  return useQuery({
    queryKey: [...ACCOUNT_MAPPINGS_QUERY_KEY, filters],
    queryFn: () => accountMappingService.getMappings(filters),
    enabled: !!filters?.template_id,
  });
}

export function useAccountMapping(id: string) {
  return useQuery({
    queryKey: [...ACCOUNT_MAPPINGS_QUERY_KEY, id],
    queryFn: () => accountMappingService.getMappingById(id),
    enabled: !!id,
  });
}

export function useCreateAccountMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AccountMappingFormData) =>
      accountMappingService.createMapping(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ACCOUNT_MAPPINGS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: MAPPING_TEMPLATES_QUERY_KEY });
      toast.success("Mapeo de cuenta creado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear mapeo: ${error.message}`);
    },
  });
}

export function useUpdateAccountMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<AccountMappingFormData>;
    }) => accountMappingService.updateMapping(id, data),
    onSuccess: (updatedMapping, { id }) => {
      queryClient.invalidateQueries({ queryKey: ACCOUNT_MAPPINGS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...ACCOUNT_MAPPINGS_QUERY_KEY, id],
      });
      toast.success("Mapeo de cuenta actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar mapeo: ${error.message}`);
    },
  });
}

export function useDeleteAccountMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountMappingService.deleteMapping(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNT_MAPPINGS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: MAPPING_TEMPLATES_QUERY_KEY });
      toast.success("Mapeo de cuenta eliminado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar mapeo: ${error.message}`);
    },
  });
}

export function useBulkUpdateAccountMappings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      updates: Array<{ id: string; data: Partial<AccountMappingFormData> }>
    ) => accountMappingService.bulkUpdateMappings(updates),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ACCOUNT_MAPPINGS_QUERY_KEY });
      if (result.errors.length > 0) {
        toast.warning(
          `Actualización parcial: ${result.updated} actualizados, ${result.errors.length} errores`
        );
      } else {
        toast.success(`${result.updated} mapeos actualizados exitosamente`);
      }
    },
    onError: (error: Error) => {
      toast.error(`Error en actualización masiva: ${error.message}`);
    },
  });
}

export function useImportAccountMappings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      companyId,
      mappings,
    }: {
      templateId: string;
      companyId: string;
      mappings: MappingImportData[];
    }) => accountMappingService.importMappings(templateId, companyId, mappings),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ACCOUNT_MAPPINGS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: MAPPING_TEMPLATES_QUERY_KEY });

      const messages: string[] = [];
      if (result.created > 0) messages.push(`${result.created} creados`);
      if (result.updated > 0) messages.push(`${result.updated} actualizados`);
      if (result.skipped > 0) messages.push(`${result.skipped} omitidos`);

      if (result.errors.length > 0) {
        toast.warning(
          `Importación completada con errores: ${messages.join(", ")}. ${result.errors.length} errores.`
        );
      } else {
        toast.success(`Importación exitosa: ${messages.join(", ")}`);
      }
    },
    onError: (error: Error) => {
      toast.error(`Error en importación: ${error.message}`);
    },
  });
}
